import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Table, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { FaPlus, FaTrash, FaDownload } from 'react-icons/fa';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  listarSolicitaDebMunicipioAnexos,
  getSolicitaDebMunicipioTitulosAnexo,
  uploadSolicitaDebMunicipioAnexo,
  anexarSolicitaDebMunicipioDocumento,
  excluirSolicitaDebMunicipioAnexo,
  baixarSolicitaDebMunicipioAnexo,
} from '~/Services/Service';

const WHITELIST_EXTENSOES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.zip', '.rar'];

// DW-1025 — "Anexar Documentos" de uma solicitação já aberta.
// No Delphi cabia UM anexo por solicitação (campo CalcIPTU.NomeArqTermo, nome fixo
// 'T{boleto}.pdf'), sem título escolhido nem exclusão. Aqui são N anexos em CalcIPTU_Anexo,
// com título obrigatório da lista fixa; título + Qd/Lt do ativo formam o nome do arquivo.
const AnexosModal = ({ show, onHide, solicitacao }) => {
  const codigo = solicitacao ? solicitacao.codigo : null;

  const [anexos, setAnexos] = useState([]);
  const [titulos, setTitulos] = useState([]);
  const [tituloSelecionado, setTituloSelecionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });
  const inputArquivoRef = useRef(null);

  useEffect(() => {
    if (show && codigo) {
      carregar();
      carregarTitulos();
      setTituloSelecionado(null);
    }
  }, [show, codigo]);

  const carregar = async () => {
    setLoading(true);
    try {
      const resp = await listarSolicitaDebMunicipioAnexos(codigo);
      setAnexos(resp || []);
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar anexos.');
    } finally {
      setLoading(false);
    }
  };

  const carregarTitulos = async () => {
    try {
      const resp = await getSolicitaDebMunicipioTitulosAnexo();
      setTitulos((resp || []).map((t) => ({ value: t, label: t })));
    } catch (err) {
      toast.error('Erro ao carregar títulos de anexo.');
    }
  };

  // O título é obrigatório e é escolhido ANTES do arquivo: ele compõe o nome final,
  // então não há como anexar primeiro e nomear depois.
  const handleClickAnexar = () => {
    if (!tituloSelecionado) {
      toast.error('Selecione o título do anexo.');
      return;
    }
    if (inputArquivoRef.current) inputArquivoRef.current.click();
  };

  const handleArquivoChange = async (e) => {
    const inputEl = e.target;
    const arquivo = (inputEl.files || [])[0];
    if (!arquivo) return;

    const ext = arquivo.name.toLowerCase().substring(arquivo.name.lastIndexOf('.'));
    if (!WHITELIST_EXTENSOES.includes(ext)) {
      toast.error(`Extensão não permitida: ${arquivo.name}`);
      inputEl.value = '';
      return;
    }

    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('file', arquivo);
      const upload = await uploadSolicitaDebMunicipioAnexo(fd);
      await anexarSolicitaDebMunicipioDocumento(
        codigo,
        upload && upload.token,
        tituloSelecionado.value
      );
      toast.success('Anexo incluído com sucesso.');
      setTituloSelecionado(null);
      carregar();
    } catch (err) {
      toast.error(err.message || 'Falha ao anexar documento.');
    } finally {
      setEnviando(false);
      inputEl.value = '';
    }
  };

  const handleBaixar = async (anexo) => {
    try {
      const blob = await baixarSolicitaDebMunicipioAnexo(codigo, anexo.seq);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', anexo.nome_arquivo);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Falha ao baixar anexo.');
    }
  };

  const handleExcluir = (anexo) => {
    setConfirmacao({ show: true, item: anexo });
  };

  // Devolve a contagem final para a listagem atualizar só o contador daquela linha, em vez de
  // refazer a busca inteira. Precisa ser um handler próprio: o `onHide` do react-bootstrap
  // (X e backdrop) é chamado com o evento, não com a contagem.
  const handleFechar = () => onHide(anexos.length);

  const handleConfirmarExclusao = async (confirmado) => {
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    if (!confirmado || !item) return;
    try {
      await excluirSolicitaDebMunicipioAnexo(codigo, item.seq);
      toast.success('Anexo excluído.');
      carregar();
    } catch (err) {
      toast.error(err.message || 'Falha ao excluir anexo.');
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleFechar} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
            Anexar Documentos{codigo ? ` — Solicitação #${codigo}` : ''}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="align-items-end mb-3 border rounded p-2 bg-light mx-0">
            <Col md={8}>
              <Form.Label className="mb-1 font-weight-bold" style={{ fontSize: '0.85rem' }}>
                Título do documento *
              </Form.Label>
              <Select
                placeholder="Selecione o título"
                isClearable
                styles={customStyles}
                components={{ DropdownIndicator }}
                options={titulos}
                value={tituloSelecionado}
                onChange={(opt) => setTituloSelecionado(opt)}
                isDisabled={enviando}
              />
            </Col>
            <Col md={4} className="text-right">
              <Button
                variant="success"
                onClick={handleClickAnexar}
                disabled={enviando || !tituloSelecionado}
              >
                <FaPlus className="mr-1" />
                {enviando ? 'Enviando...' : 'Anexar Documento'}
              </Button>
              <input
                type="file"
                ref={inputArquivoRef}
                style={{ display: 'none' }}
                onChange={handleArquivoChange}
                accept={WHITELIST_EXTENSOES.join(',')}
              />
            </Col>
          </Row>

          {loading ? (
            <div className="py-3"><Loading /></div>
          ) : (
            <Table striped bordered hover size="sm" className="mb-0">
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <th style={{ width: 60 }}>Seq</th>
                  <th>Documento</th>
                  <th style={{ width: 120 }}>Data</th>
                  <th style={{ width: 110 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {anexos.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">
                      Nenhum documento anexado.
                    </td>
                  </tr>
                ) : (
                  anexos.map((anexo) => (
                    <tr key={anexo.seq}>
                      <td>{anexo.seq}</td>
                      <td>{anexo.nome_arquivo}</td>
                      <td>
                        {anexo.data
                          ? new Date(anexo.data).toLocaleDateString('pt-BR')
                          : '-'}
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mr-1"
                          title="Baixar"
                          onClick={() => handleBaixar(anexo)}
                        >
                          <FaDownload />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Excluir"
                          onClick={() => handleExcluir(anexo)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleFechar}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalConfirmacao
        show={confirmacao.show}
        title="Excluir Anexo"
        message={
          confirmacao.item
            ? `Confirma a exclusão do anexo "${confirmacao.item.nome_arquivo}"? O documento sai da solicitação, mas o arquivo permanece no repositório.`
            : ''
        }
        onClose={handleConfirmarExclusao}
      />
    </>
  );
};

export default AnexosModal;
